import PropTypes from "prop-types";
import { memo } from "react";
import { useDispatch } from "react-redux";

import { TRACK_EVENT_CATEGORY_ROUTE_HEADER } from "../constants";
import { setStopSequence, trackEvent } from "../model/actions";
import AppPropTypes from "../model/propTypes";
import hasRealtimeDeparture from "../utils/hasRealtimeDeparture";

import FollowVehicleButton from "./FollowVehicleButton";
import LineIconSvg from "./LineIconSvg";
import NoRealtimeIcon from "./NoRealtimeIcon";
import RouteOtherDestinationIcon from "./RouteOtherDestinationIcon";

import "./RouteHeader.scss";

const propTypes = {
  lineInfos: AppPropTypes.lineInfos.isRequired,
  onClick: PropTypes.func,
  renderExtraBt: PropTypes.func,
};

const renderButton = (className, style, onClick, children) => {
  return onClick ? (
    <div
      className={`bt-reset ${className}`}
      onClick={onClick}
      onKeyPress={(evt) => evt.which === 13 && onClick()}
      role="button"
      style={style}
      tabIndex={0}
    >
      {children}
    </div>
  ) : (
    <div className={className} style={style}>
      {children}
    </div>
  );
};

function RouteHeader({ lineInfos, onClick, renderExtraBt }) {
  const dispatch = useDispatch();

  if (!lineInfos || !lineInfos.line) {
    return null;
  }

  const { destination, id, line, stations } = lineInfos;
  let realDestination = destination;
  if (Array.isArray(destination)) {
    [realDestination] = destination;
  }
  // When we get some falsy train, stations is null.
  const finalStop =
    stations && [...stations].reverse().find((station) => !station.cancelled);
  if (finalStop) {
    realDestination = finalStop.stationName;
  }

  return renderButton(
    "route-header",
    {
      borderColor: line.stroke,
    },
    onClick,
    <div className="destinations">
      <div className="destination">
        <span className="real-destination">
          <LineIconSvg line={line} />
          <span>{realDestination || id}</span>
          {((!lineInfos.departure && !lineInfos.has_realtime) ||
            (lineInfos.departure &&
              !hasRealtimeDeparture(lineInfos.departure))) && (
            <NoRealtimeIcon
              data-testid="norealtime-icon"
              useFzo={
                !!(
                  !lineInfos.departure ||
                  (lineInfos.departure &&
                    lineInfos.departure.operator_provides_fzo)
                )
              }
            />
          )}
        </span>
        {destination !== realDestination && (
          <span className="cancelled-destination">{`${destination}`}</span>
        )}
        <div className="route-header-buttons">
          <FollowVehicleButton line={line} trainId={lineInfos.train_id} />
          {renderExtraBt && renderExtraBt()}
        </div>
      </div>
      {(lineInfos.otherStopSequences || []).map((seq) => {
        if (seq.destination === lineInfos.destination) {
          return null;
        }
        return (
          <button
            className="bt-reset other-destination"
            key={seq.destination}
            onClick={() => {
              dispatch(setStopSequence(seq));
              dispatch(
                trackEvent(
                  TRACK_EVENT_CATEGORY_ROUTE_HEADER,
                  "selectWingTrain",
                  `${seq.longName || seq.shortName} ${seq.destination}`,
                ),
              );
            }}
            type="button"
          >
            <RouteOtherDestinationIcon color={line.color} small />
            <LineIconSvg line={line} />
            <span>{seq.destination}</span>
          </button>
        );
      })}
    </div>,
  );
}

RouteHeader.propTypes = propTypes;

export default memo(RouteHeader);
